import React, { useState, useCallback } from 'react';
import { useMutation } from 'redux-query-react';
import { RouteComponentProps } from '@reach/router';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { DateTime } from 'luxon';
import { FormikProps, withFormik, WithFormikConfig } from 'formik';
import { Text, Button, Icon, LinkButton, HeaderText } from '@el8/vital';

import { PaymentCharge } from 'EntityTypes';
import { Box } from 'components';
import CheckboxFormik from 'components/formik/CheckboxFormik';
import { PaymentChargeStatus } from 'constants/payments';
import { confirmPaymentChargeQuery } from 'modules/payments';
import { formatDollars, notify } from 'utils';
import { fixedSubmitForm } from 'utils/formik';
import { checkSuccess } from 'utils/errors';

import RootContainer from '../shared/RootContainer';
import PaymentsHeader from '../shared/PaymentsHeader';
import PaymentMethodForm, {
  PaymentMethodFormValues,
  paymentMethodInitialValues,
  paymentMethodValidationSchema,
} from '../shared/PaymentMethodForm';
import {
  makeConfirmCardPaymentData,
  useSaveCard,
  useConfirmStripeCardPayment,
} from '../shared/helpers';
import styles from './PaymentChargePage.less';

type Values = PaymentMethodFormValues & {
  save_card: boolean; // This field is only used for failed charges
};

type NonFormikProps = RouteComponentProps<{
  paymentChargeId: string;
}>;

type Props = NonFormikProps & FormikProps<Values>;

const withFormikConfig: WithFormikConfig<NonFormikProps, Values> = {
  mapPropsToValues() {
    return { ...paymentMethodInitialValues, save_card: true };
  },
  handleSubmit() {
    // Required by Typescript, but we can't actually perform the submission here because
    // we can't access `stripe` (from useStripe) or `elements` (from useElements) inside of
    // this function. So instead, we handle submission inside the component.
  },
  validationSchema: paymentMethodValidationSchema,
};

const PaymentChargePage: React.FC<Props> = ({
  isSubmitting,
  location,
  paymentChargeId,
  setSubmitting,
  submitForm,
  validateForm,
  values,
}) => {
  const {
    patientId,
    patientKey,
    patientLiabilityExternalMemo,
    patientLiabilityReasons,
    patientName,
    paymentCharge,
    paymentIntent,
  } = window.elate;

  const stripe = useStripe();
  const elements = useElements();
  const isStripeInitialized = Boolean(stripe && elements);

  const [paymentMethod, setPaymentMethod] = useState(window.elate.paymentMethod);
  const [isUpdatingCard, setUpdatingCard] = useState(false);

  const handleStartUpdatingCard = useCallback(() => {
    setUpdatingCard(true);
  }, []);

  const handleCancelUpdatingCard = useCallback(() => {
    setUpdatingCard(false);
  }, []);

  const handleConfirmUpdatingCard = useSaveCard(patientId, patientKey, (newPaymentMethod) => {
    setPaymentMethod(newPaymentMethod);
    setUpdatingCard(false);
    notify('Card updated.');
  });

  const key = new URLSearchParams(location.search).get('key');
  const [, confirmPaymentCharge] = useMutation(() =>
    confirmPaymentChargeQuery(Number(paymentChargeId), {
      key,
      save_payment_method: values.save_card,
      stripe_payment_intent_id: paymentIntent?.id,
    }),
  );

  const confirmStripeCardPayment = useConfirmStripeCardPayment();
  const handlePay = async (): Promise<void> => {
    if (!isStripeInitialized) return;

    let isSuccess = false;
    try {
      // "Submit" form to run validation and touch all inputs. See `handleSubmit` in this
      // component's Formik config for more info on why we do this here.
      await fixedSubmitForm({ submitForm, validateForm });

      try {
        const stripeSuccess = await confirmStripeCardPayment(
          paymentIntent?.client_secret,
          makeConfirmCardPaymentData(elements, values),
        );
        if (!stripeSuccess) return;

        const paymentChargeResponse = await confirmPaymentCharge();
        checkSuccess(paymentChargeResponse);

        window.location.href = (paymentChargeResponse.body as PaymentCharge).receipt_page_url;
        isSuccess = true;
      } catch (err) {
        notify('There was an error making the payment. Please try again.');
      }
    } finally {
      // If the entire flow succeeds, we don't want to set submitting back to false.
      // We want everything to stay disabled while the user is redirected to the receipt page,
      // so the user can't click Pay again while the redirect is in progress
      if (!isSuccess) {
        setSubmitting(false);
      }
    }
  };

  let title;
  let datePrefix;
  let secondaryDateLabel;
  let secondaryDateText;
  let paymentMethodDescription = paymentMethod?.payment_method_description;
  switch (paymentCharge.status) {
    case PaymentChargeStatus.Completed:
    case PaymentChargeStatus.CompletedCash:
      title = 'Payment Receipt';
      datePrefix = 'Paid on';
      paymentMethodDescription = paymentCharge.payment_method_description;
      break;
    case PaymentChargeStatus.Pending:
      title = 'Payment Summary';
      datePrefix = 'Created on';
      secondaryDateLabel = 'Date paid';
      secondaryDateText = 'Pending';
      break;
    case PaymentChargeStatus.Refunded: {
      title = 'Payment Refunded';
      datePrefix = 'Refunded on';
      secondaryDateLabel = 'Original date paid';
      secondaryDateText = DateTime.fromISO(paymentCharge.completed_at).toLocaleString(
        // @ts-expect-error: el8-migrate DO NOT COPY PASTE THIS LINE TO SILENCE YOUR OWN ERRORS.
        // This was added to silence existing errors when upgrading to typescript 4.5.4
        DateTime.DATE_SHORT,
      );
      break;
    }
    case PaymentChargeStatus.Failed:
      title = 'Payment Request';
      datePrefix = 'Requested on';
      break;
    case PaymentChargeStatus.DoNotComplete:
      // TODOSIDNEY: handle this status. No decision yet on how this works
      title = 'Payment Charge';
      break;
    default:
      break;
  }

  const isCardPayment = paymentCharge.payment_type === 'card';
  const isFailedCharge = paymentCharge.status === PaymentChargeStatus.Failed;

  return (
    <RootContainer>
      <PaymentsHeader />
      <HeaderText align="center" legacyVariant="legacyHeaderText" tag="h1">
        {title}
      </HeaderText>
      <Text style={{ marginBottom: 24 }} align="center" tag="div">
        {datePrefix}{' '}
        {/* @ts-expect-error: el8-migrate DO NOT COPY PASTE THIS LINE TO SILENCE YOUR OWN ERRORS. This was added to silence existing errors when upgrading to typescript 4.5.4 */}
        {DateTime.fromISO(paymentCharge.last_status_change_at).toLocaleString(DateTime.DATE_SHORT)}
      </Text>
      <Text tag="div" textSize="large" weight="medium">
        Patient name
      </Text>
      <Text style={{ marginBottom: 24 }} tag="p" textSize="large">
        {patientName}
      </Text>
      <Text tag="div" textSize="large" weight="medium">
        {isFailedCharge ? 'Amount' : 'Charge'}
      </Text>
      <Text style={{ marginBottom: 24 }} tag="p" textSize="large">
        {formatDollars(paymentCharge.amount_usd)}
      </Text>
      {patientLiabilityReasons.length > 0 && (
        <>
          <Text tag="div" textSize="large" weight="medium">
            Reason
          </Text>
          <Text style={{ marginBottom: 24 }} tag="p" textSize="large">
            {patientLiabilityReasons.join(', ')}
          </Text>
        </>
      )}
      {patientLiabilityExternalMemo.length > 0 && (
        <>
          <Text tag="div" textSize="large" weight="medium">
            Note
          </Text>
          <Text style={{ marginBottom: 24 }} tag="p" textSize="large">
            {patientLiabilityExternalMemo.join(', ')}
          </Text>
        </>
      )}
      <Text tag="div" textSize="large" weight="medium">
        Payment method
      </Text>
      <Box direction="row" style={{ marginBottom: 24 }}>
        {isCardPayment && <Icon icon="credit-card" style={{ marginRight: 8, marginTop: 2 }} />}
        <Text tag="div" textSize="large" style={{ flex: 1 }}>
          {paymentMethodDescription}
          {isFailedCharge && (
            <Text className={styles.error} tag="div">
              Declined, please update your card
            </Text>
          )}
        </Text>
        {isCardPayment && paymentCharge.status === PaymentChargeStatus.Pending && (
          <LinkButton
            disabled={isSubmitting}
            onClick={isUpdatingCard ? handleCancelUpdatingCard : handleStartUpdatingCard}
            text={isUpdatingCard ? 'Cancel update' : 'Update card'}
          />
        )}
      </Box>
      {isUpdatingCard || isFailedCharge ? (
        <>
          <PaymentMethodForm />
          {isFailedCharge && (
            <CheckboxFormik
              label={`Keep this card on file for ${patientName} (You will always be notified before any charges)`}
              name="save_card"
            />
          )}
          <Button
            kind="primary"
            disabled={!isStripeInitialized}
            loading={isSubmitting}
            onClick={isFailedCharge ? handlePay : handleConfirmUpdatingCard}
            text={isFailedCharge ? 'Pay' : 'Update card'}
            style={{ width: '100%' }}
          />
        </>
      ) : (
        <>
          {secondaryDateLabel && (
            <>
              <Text tag="div" textSize="large" weight="medium">
                {secondaryDateLabel}
              </Text>
              <Text style={{ marginBottom: 24 }} tag="p" textSize="large">
                {secondaryDateText}
              </Text>
            </>
          )}
          <Text tag="div">
            If you have questions about these charges, please reach out directly to the practice.
          </Text>
        </>
      )}
    </RootContainer>
  );
};

export default withFormik(withFormikConfig)(PaymentChargePage);
